import { createServerFn } from "@tanstack/react-start";
import { asc } from "drizzle-orm";
import z from "zod";
import { db } from "~/db";
import { infraType } from "~/db/schema";

export type InfraType = {
  id: number;
  name: string;
};

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
let cachedInfraTypes: { data: InfraType[]; timestamp: number } | null = null;

const GetInfraTypesSchema = z.object({
  ids: z.array(z.number()).optional(),
  names: z.array(z.string()).optional(),
});

export const getInfraTypes = createServerFn({ method: "GET" })
  .inputValidator(GetInfraTypesSchema.optional())
  .handler(async (ctx) => {
    const ids = ctx.data?.ids;
    const names = ctx.data?.names;

    const now = Date.now();
    if (cachedInfraTypes && now - cachedInfraTypes.timestamp < CACHE_TTL_MS) {
      console.log("Serving infra types from cache.");
      const types = cachedInfraTypes.data;

      // If names are provided, convert them to IDs first
      let finalIds = ids;
      if (names && names.length > 0) {
        const nameMap = new Map(types.map((type) => [type.name, type.id]));
        finalIds = names
          .map((name) => nameMap.get(name))
          .filter((id): id is number => id !== undefined);
      }

      // If IDs are provided (either directly or converted from names), filter from cache and preserve order
      if (finalIds && finalIds.length > 0) {
        const typeMap = new Map(types.map((type) => [type.id, type]));
        return finalIds
          .map((id) => typeMap.get(id))
          .filter((type): type is InfraType => type !== undefined);
      }

      return types;
    }
    if (cachedInfraTypes && now - cachedInfraTypes.timestamp >= CACHE_TTL_MS) {
      cachedInfraTypes = null;
    }

    console.log("Cache miss. Fetching infra types from database...");
    const allTypes = await db
      .select({
        id: infraType.id,
        name: infraType.name,
      })
      .from(infraType)
      .groupBy(infraType.id, infraType.name)
      .orderBy(asc(infraType.id));

    cachedInfraTypes = { data: allTypes, timestamp: Date.now() };

    // If names are provided, convert them to IDs first
    let finalIds = ids;
    if (names && names.length > 0) {
      const nameMap = new Map(
        allTypes.map((type) => [type.name, type.id])
      );
      finalIds = names
        .map((name) => nameMap.get(name))
        .filter((id): id is number => id !== undefined);
    }

    // If IDs are provided (either directly or converted from names), filter the results and preserve order
    if (finalIds && finalIds.length > 0) {
      const typeMap = new Map(
        allTypes.map((type) => [type.id, type])
      );
      return finalIds
        .map((id) => typeMap.get(id))
        .filter((type): type is InfraType => type !== undefined);
    }

    return allTypes;
  });
